import { PropsWithChildren, useEffect, useState } from 'react';
import SettingsContext, { Settings, SettingsContextProps } from '@/contexts/SettingsContext';
import useUserQuery from '@/modules/settings/hooks/useUserQuery';

interface SettingsProviderProps extends PropsWithChildren {
  initialSettings?: Settings;
}

const defaultSettings: Settings = {
  currencyCode: 'SGD',
  timezone: 'Asia/Singapore',
  languageCode: 'en',
  locale: 'en-SG',
};

const SettingsProvider = ({ children, initialSettings }: SettingsProviderProps) => {
  const { user, isLoading, invalidateQuery } = useUserQuery();

  const [settings, setSettings] = useState<Settings>(initialSettings || defaultSettings);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    setSettings((prev) => {
      const next = {
        ...prev,
        currencyCode: user?.currencyCode || prev.currencyCode,
        timezone: user?.timezone || prev.timezone,
        languageCode: user?.languageCode || prev.languageCode,
        isInitialized: true,
      };

      const hasChanged =
        prev.currencyCode !== next.currencyCode ||
        prev.timezone !== next.timezone ||
        prev.languageCode !== next.languageCode ||
        prev.isInitialized !== next.isInitialized;

      return hasChanged ? next : prev;
    });
  }, [user, isLoading]);

  const updateSettings = async (updatedSettings: Partial<Settings>) => {
    setSettings((prev) => ({
      ...prev,
      ...updatedSettings,
    }));
  };

  const resetSettings = async () => {
    await invalidateQuery();
  };

  const contextValue: SettingsContextProps = {
    ...settings,
    updateSettings,
    resetSettings,
  };

  if (!settings.isInitialized) {
    return null;
  }

  return <SettingsContext.Provider value={contextValue}>{children}</SettingsContext.Provider>;
};

export default SettingsProvider;
